import { AppConfig } from "AppConfig";
import { getRequestHeaders } from "msal/MsalAuthProvider";
import { createLog } from "_data/services/extract/service";

/**
 * Payment errors come back as a 400 whose body is the bare error code, so callers can
 * branch on it. Returns null when there is nothing readable.
 */
const readErrorCode = (response) =>
  response
    .text()
    .then((text) => text?.replace(/^"|"$/g, "").trim() || null)
    .catch(() => null);

/**
 * Reads a JSON response, failing on the HTTP status rather than on a `status` field in the body.
 *
 * These four endpoints used to do `response.json()` and then test `response.status >= 400` on the
 * *parsed body*, which is not the HTTP status: a 500 whose body happens to be JSON without a
 * `status` field was reported as success, and the value handed to the caller was the error object.
 * It only half-worked because a non-JSON error body makes `json()` throw into the catch.
 */
const readJson = (url, response, callback, label) => {
  if (!response.ok) {
    callback(false, null);
    createLog("Error", `${label} Status: ${response.status}`, url);
    return null;
  }

  return response.json().then((body) => {
    callback(true, body);
  });
};

export const getBillingAddressRequest = (callback) => {
  const url = `${AppConfig.SERVICE_BASE_URL}/api/v1/payment/billingaddress`;

  fetch(url, {
    method: "GET",
    headers: getRequestHeaders(),
  })
    .then((response) =>
      readJson(url, response, callback, "Get Billing Address"),
    )
    .catch((error) => {
      callback(false, null);
      createLog("Error", `Get Billing Address: ${error}`);
    });
};

/**
 * Replaces the whole billing address. A PUT, not a POST: there is one address per user and
 * the call is idempotent, so a retry after a timeout is always safe.
 */
export const setBillingAddressRequest = (body, callback) => {
  const url = `${AppConfig.SERVICE_BASE_URL}/api/v1/payment/billingaddress`;
  const reqBody = JSON.stringify(body);

  fetch(url, {
    method: "PUT",
    headers: getRequestHeaders(),
    body: reqBody,
  })
    .then((response) => {
      if (!response.ok) {
        callback(false, null);
        createLog(
          "Error",
          `Set Billing Address Status: ${response.status}`,
          url,
          reqBody,
        );
        return null;
      }
      return response.json().then((body) => {
        callback(true, body);
      });
    })
    .catch((error) => {
      callback(false, null);
      createLog("Error", `Set Billing Address : ${error}`, url, reqBody);
    });
};

export const getBillingHistoryRequest = (callback) => {
  const url = `${AppConfig.SERVICE_BASE_URL}/api/v1/payment/billinghistory`;

  fetch(url, {
    method: "GET",
    headers: getRequestHeaders(),
  })
    .then((response) =>
      readJson(url, response, callback, "Get Billing History"),
    )
    .catch((error) => {
      callback(false, null);
      createLog("Error", `Get Billing History: ${error}`);
    });
};

/**
 * Subscribes, resumes a cancelled subscription, or changes the plan -- the backend works
 * out which from the user's current state. On failure the callback receives the error code
 * so the caller can send the user to the billing form instead of showing an error.
 */
export const initiatePaymentSubscriptionRequest = (type, callback) => {
  const url = `${AppConfig.SERVICE_BASE_URL}/api/v1/payment/subscription/premium/${type}`;

  fetch(url, {
    method: "POST",
    headers: getRequestHeaders(),
  })
    .then((response) => {
      if (!response.ok) {
        return readErrorCode(response).then((errorCode) => {
          callback(false, errorCode);
          createLog(
            "Error",
            `Initiate Subscription Payment Status: ${response.status} ${errorCode}`,
          );
        });
      }
      return response.json().then((response) => {
        callback(true, response);
      });
    })
    .catch((error) => {
      callback(false, null);
      createLog("Error", `Initiate Subscription Payment: ${error}`);
    });
};

export const switchPaymentSubscriptionRequest = (type, callback) => {
  const url = `${AppConfig.SERVICE_BASE_URL}/api/v1/payment/subscription/premium/${type}`;

  fetch(url, {
    method: "PATCH",
    headers: getRequestHeaders(),
  })
    .then((response) => {
      if (!response.ok) {
        return readErrorCode(response).then((errorCode) => {
          callback(false, errorCode);
          createLog(
            "Error",
            `Switch Subscription Payment Status: ${response.status} ${errorCode}`,
          );
        });
      }
      return callback(true, null);
    })
    .catch((error) => {
      callback(false, null);
      createLog("Error", `Switch Subscription Payment: ${error}`);
    });
};

/**
 * Cancels the subscription at the end of the current period. Note what does *not* change:
 * the subscription stays Active and the licence keeps its expiration -- only
 * subscriptionCancelAtPeriodEnd flips, so callers must re-read user info rather than wait
 * for a status change.
 */
export const unsubscribePaymentRequest = (callback) => {
  const url = `${AppConfig.SERVICE_BASE_URL}/api/v1/payment/subscription/premium`;

  fetch(url, {
    method: "DELETE",
    headers: getRequestHeaders(),
  })
    .then((response) => {
      if (!response.ok) {
        return readErrorCode(response).then((errorCode) => {
          callback(false, errorCode);
          createLog(
            "Error",
            `Unsubscribe Payment Status: ${response.status} ${errorCode}`,
          );
        });
      }
      return callback(true, null);
    })
    .catch((error) => {
      callback(false, null);
      createLog("Error", `Unsubscribe Payment: ${error}`);
    });
};

/**
 * Reads the plan list and its prices.
 *
 * This previously reported success unconditionally, so an error body reached the caller as if it
 * were the plan list -- and since the caller does `.find()` over it, a non-array body threw and
 * the price on the account card rendered blank.
 */
export const getSubscriptionTypesRequest = (callback) => {
  const url = `${AppConfig.SERVICE_BASE_URL}/api/v1/payment/subscription/premium`;

  fetch(url, {
    method: "GET",
    headers: getRequestHeaders(),
  })
    .then((response) =>
      readJson(url, response, callback, "Get Subscription Types"),
    )
    .catch((error) => {
      callback(false, null);
      createLog("Error", `Get Subscription Types : ${error}`);
    });
};

/**
 * Reads the current state of an order: `status`, `currentPeriodEnd`, `cancelAtPeriodEnd` and
 * the amounts. A GET, because it reads and changes nothing -- arriving back from the provider
 * grants nothing, and the browser carries nothing this endpoint would trust.
 *
 * `status === 'Active'` means the payment has been recorded. The confirming webhook travels on
 * a separate connection and may not have arrived yet, which is why callers poll this -- and
 * why a slow answer must never be presented as a failed payment.
 *
 * Replaces `POST {orderId}/success`, which took the provider's query string in the body, and
 * `GET {orderId}/callback`. Both are gone.
 */
export const getOrderStateRequest = (id, callback) => {
  const url = `${AppConfig.SERVICE_BASE_URL}/api/v1/payment/${id}/state`;

  fetch(url, {
    method: "GET",
    headers: getRequestHeaders(),
  })
    .then((response) => {
      if (!response.ok) {
        return readErrorCode(response).then((errorCode) => {
          callback(false, errorCode);
          createLog(
            "Error",
            `Get Order State Status: ${response.status} ${errorCode}`,
            url,
          );
        });
      }
      return response.json().then((response) => {
        callback(true, response);
      });
    })
    .catch((error) => {
      callback(false, null);
      createLog("Error", `Get Order State : ${error}`, url);
    });
};

/**
 * Abandons an order whose checkout was never completed. Named for what it does: this closes an
 * unpaid order and *cannot* end a live subscription -- that is `unsubscribePaymentRequest`.
 * Replaces `POST {orderId}/cancel`, which was ambiguous about which of the two it meant.
 */
export const abandonOrderRequest = (id, callback) => {
  const url = `${AppConfig.SERVICE_BASE_URL}/api/v1/payment/${id}/abandon`;

  fetch(url, {
    method: "POST",
    headers: getRequestHeaders(),
  })
    .then((response) => {
      if (!response.ok) {
        return readErrorCode(response).then((errorCode) => {
          callback(false, errorCode);
          createLog(
            "Error",
            `Abandon Order Status: ${response.status} ${errorCode}`,
            url,
          );
        });
      }
      return callback(true, null);
    })
    .catch((error) => {
      callback(false, null);
      createLog("Error", `Abandon Order : ${error}`, url);
    });
};
